/**
 * ProviderRegistry.js
 *
 * Ordered registry of search data providers.
 * Providers are returned in registration order, which controls section
 * order in the search modal.
 *
 * Provider interface:
 * {
 *   id:        string        — unique key (matches section key in modal)
 *   label:     string        — section header display name
 *   icon:      LucideIcon
 *   iconBg:    string        — Tailwind bg-* class
 *   iconColor: string        — Tailwind text-* class
 *   fields:    string[]      — raw-item fields to index and rank against
 *   load:      (districtId: string) => Promise<Object[]>
 *   buildItem: (raw: Object) => SearchItem
 * }
 */

class ProviderRegistry {
  #providers = new Map();

  /**
   * Register a provider. Returns `this` for chaining.
   * Re-registering with the same id replaces the existing entry.
   *
   * @param {Object} provider
   * @returns {ProviderRegistry}
   */
  register(provider) {
    if (!provider?.id) throw new Error("Provider must have an id");
    this.#providers.set(provider.id, provider);
    return this;
  }

  /** @returns {Object[]} all registered providers in insertion order */
  getAll() {
    return Array.from(this.#providers.values());
  }

  /**
   * @param {string} id
   * @returns {Object|undefined}
   */
  getById(id) {
    return this.#providers.get(id);
  }

  /** Remove a provider by id. */
  unregister(id) {
    this.#providers.delete(id);
    return this;
  }

  get size() {
    return this.#providers.size;
  }
}

export const registry = new ProviderRegistry();
